import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { AppWrapper, GridView, Subtitle, Title, UndoSnackbar, UsOnlyPill } from '../../components/_mui';
import {
  ADD_ITEM_TO_CONTINUE_TEXT,
  BACK_ARIA_LABEL,
  BACK_TO_TOP_LABEL,
  BROWSE_MARKETPLACE_LABEL,
  CONTINUE_TO_SHIPPING_LABEL,
  getBudgetIntroText,
  getItemCountText,
  getItemsAddedText,
  getPicksReplacedText,
  MARKETPLACE_GRID_COLUMNS,
  MARKETPLACE_GRID_GAP,
  MARKETPLACE_INFINITE_SCROLL_THRESHOLD,
  MARKETPLACE_SHIPPING_INTRO_LEAD,
  MARKETPLACE_SHIPPING_INTRO_TAIL,
  MARKETPLACE_TOTAL_CREDITS,
} from '../../constants/marketplace';
import { ROUTE_NAMES } from '../../constants/routes';
import { useFab } from '../../hooks/useFab';
import { useFooter } from '../../hooks/useFooter';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import useWindowSize from '../../hooks/useWindowSize';
import {
  marketplaceAddDirectRequestAction,
  marketplaceCatalogRequestAction,
  marketplaceCloseDetailsAction,
  marketplaceDropReplacedCartAction,
  marketplaceOpenDetailsAction,
  marketplaceProductRequestAction,
  marketplaceRemoveFromCartAction,
  marketplaceRestoreCartAction,
  marketplaceSetCartOpenAction,
  marketplaceSetFiltersAction,
  marketplaceSetSupplierFilterAction,
  marketplaceSwapCancelAction,
} from '../../store/actions/marketplace';
import { selectGreetingTitle, selectHasPicksToReturnTo } from '../../store/selectors/derived';
import { selectIsEmbed } from '../../store/selectors/init';
import {
  selectCartCount,
  selectCheckingProductId,
  selectHasNextPage,
  selectIsCartOpen,
  selectIsCatalogResultStale,
  selectIsMarketplaceLoading,
  selectIsMarketplaceLoadingMore,
  selectIsSupplierFilterOn,
  selectMarketplaceCart,
  selectMarketplaceError,
  selectMarketplaceItemCount,
  selectMarketplaceItems,
  selectOpenProductId,
  selectReplacedCart,
  selectResultCatalogContext,
  selectResultCategoryName,
  selectSupplierLock,
  selectVisibleMarketplaceItems,
} from '../../store/selectors/marketplace';
import { IMarketplaceCatalogItem } from '../../types/marketplace';
import {
  canAffordCredits,
  doesCartFit,
  getCartLineForProduct,
  hasSpentCredits,
  isProductInCart,
} from '../../util/credits';
import { isOtherSupplier } from '../../util/marketplace';
import { MarketplaceCartDrawer } from './components/MarketplaceCartDrawer';
import { MarketplaceDetailsDrawer } from './components/MarketplaceDetailsDrawer';
import { MarketplaceEmptyState } from './components/MarketplaceEmptyState';
import { MarketplaceItemCard } from './components/MarketplaceItemCard';
import { MarketplaceToolbar } from './components/MarketplaceToolbar';
import { SupplierLockBanner } from './components/SupplierLockBanner';

const getProductKey = (item: IMarketplaceCatalogItem) => item.product_id;

const introTextSx: SxProps<Theme> = { color: ({ palette }) => darken(palette.text.secondary, 0.15), my: 0 };

const introShippingIconSx: SxProps<Theme> = {
  fontSize: 18,
  color: 'info.main',
  verticalAlign: 'text-bottom',
  mr: 0.5,
};

const introShippingTailSx: SxProps<Theme> = { color: 'text.primary', fontWeight: 'medium' };

const titleBackButtonSx: SxProps<Theme> = { color: 'text.primary', ml: -1 };

const picksLinkSx: SxProps<Theme> = {
  fontSize: 14,
  fontWeight: 'medium',
  '&:hover': { backgroundColor: ({ palette }) => alpha(palette.primary.main, 0.08) },
};

const countRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  mt: 2,
  mb: 1.5,
};

const loadingSx: SxProps<Theme> = { display: 'flex', justifyContent: 'center', py: 8 };

const loadingMoreSx: SxProps<Theme> = { display: 'flex', justifyContent: 'center', mt: 3 };

const gridWrapperSx: SxProps<Theme> = { paddingBottom: 7 };

const Marketplace = () => {
  const theme = useTheme();
  const { mobile } = useWindowSize();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  const loadedItems = useSelector(selectMarketplaceItems);
  const items = useSelector(selectVisibleMarketplaceItems);
  const isSupplierFilterOn = useSelector(selectIsSupplierFilterOn);
  const itemCount = useSelector(selectMarketplaceItemCount);
  const resultContext = useSelector(selectResultCatalogContext);
  const resultCategoryName = useSelector(selectResultCategoryName);
  const isResultStale = useSelector(selectIsCatalogResultStale);
  const cart = useSelector(selectMarketplaceCart);
  const cartCount = useSelector(selectCartCount);
  const isLoading = useSelector(selectIsMarketplaceLoading);
  const isLoadingMore = useSelector(selectIsMarketplaceLoadingMore);
  const error = useSelector(selectMarketplaceError);
  const hasNextPage = useSelector(selectHasNextPage);
  const hasPicksToReturnTo = useSelector(selectHasPicksToReturnTo);
  const isEmbed = useSelector(selectIsEmbed);
  const checkingProductId = useSelector(selectCheckingProductId);
  const openProductId = useSelector(selectOpenProductId);
  const isCartOpen = useSelector(selectIsCartOpen);
  const supplierLock = useSelector(selectSupplierLock);
  const replacedCart = useSelector(selectReplacedCart);

  // Remounts the snackbar per replace so a second replace gets a full timeout.
  const [replaceKey, setReplaceKey] = useState(0);

  useEffect(() => {
    if (replacedCart) {
      setReplaceKey((key) => key + 1);
    }
  }, [replacedCart]);

  const greetingTitleSelector = useMemo(() => selectGreetingTitle(pathname), [pathname]);
  const greetingTitle = useSelector(greetingTitleSelector);

  const selectionSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEmbed) {
      dispatch(marketplaceCatalogRequestAction());
    }
  }, [dispatch, isEmbed]);

  const handleFetchNextPage = useCallback(() => {
    dispatch(marketplaceCatalogRequestAction({ isLoadMore: true }));
  }, [dispatch]);

  const isCatalogEmpty = items.length === 0;
  // Loaded items exist but none ship from the locked supplier: reads as a narrowed search once
  // the catalog is exhausted; before that the next page may still carry some.
  const isEmptiedBySupplierFilter = isSupplierFilterOn && isCatalogEmpty && loadedItems.length > 0;
  // A failed page here would otherwise refetch on every render behind a spinner.
  const hasFilteredPageFailed = isEmptiedBySupplierFilter && !!error;
  const isAwaitingFilteredPage = isEmptiedBySupplierFilter && hasNextPage && !error;

  useInfiniteScroll({
    hasNextPage: hasNextPage && !hasFilteredPageFailed,
    isFetchingNextPage: isLoadingMore,
    fetchNextPage: handleFetchNextPage,
    threshold: MARKETPLACE_INFINITE_SCROLL_THRESHOLD,
    recheckKey: items.length,
  });

  const handleRetry = useCallback(() => dispatch(marketplaceCatalogRequestAction()), [dispatch]);

  const handleOpenCart = useCallback(() => dispatch(marketplaceSetCartOpenAction(true)), [dispatch]);

  const handleCloseCart = useCallback(() => {
    dispatch(marketplaceSetCartOpenAction(false));
    dispatch(marketplaceSwapCancelAction());
  }, [dispatch]);

  const handleRemovePick = useCallback(
    (productId: string) => {
      const line = getCartLineForProduct(productId, cart);

      if (line) {
        dispatch(marketplaceRemoveFromCartAction(line.variant_id));
      }
    },
    [dispatch, cart],
  );

  const handleOpenDetails = useCallback(
    (productId: string) => {
      dispatch(marketplaceProductRequestAction(productId));
      dispatch(marketplaceOpenDetailsAction(productId));
    },
    [dispatch],
  );

  const handleCloseDetails = useCallback(() => dispatch(marketplaceCloseDetailsAction()), [dispatch]);

  const picksReplacedText = useMemo(() => getPicksReplacedText(supplierLock?.name), [supplierLock?.name]);

  const handleUndoReplace = useCallback(() => dispatch(marketplaceRestoreCartAction()), [dispatch]);

  const handleCloseSnackbar = useCallback(() => dispatch(marketplaceDropReplacedCartAction()), [dispatch]);

  // The snackbar's timer dies with the page, so the Undo offer must too.
  useEffect(
    () => () => {
      dispatch(marketplaceDropReplacedCartAction());
    },
    [dispatch],
  );

  // The saga decides once details resolve whether a single-variant product skips the sidebar.
  const handleAddDirect = useCallback(
    (productId: string) => dispatch(marketplaceAddDirectRequestAction(productId)),
    [dispatch],
  );

  const handleClearFilters = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '', category: '', fitsBudget: false }));
    dispatch(marketplaceSetSupplierFilterAction(false));
  }, [dispatch]);

  const handleShowAllItems = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ fitsBudget: false }));
  }, [dispatch]);

  const handleShowAllCategories = useCallback(() => {
    dispatch(marketplaceSetFiltersAction({ query: '', category: '' }));
  }, [dispatch]);

  const handleContinue = useCallback(() => {
    dispatch(marketplaceSetCartOpenAction(false));
    history.push(ROUTE_NAMES.address);
  }, [dispatch, history]);

  const handleBackToPicks = useCallback(() => {
    history.push(ROUTE_NAMES.pyg);
  }, [history]);

  const renderCardItem = useCallback(
    (item: IMarketplaceCatalogItem) => (
      <MarketplaceItemCard
        item={item}
        inCart={isProductInCart(item.product_id, cart)}
        canAdd={canAffordCredits(item.creditPrice, cart)}
        hasPicks={cart.length > 0}
        isChecking={checkingProductId === item.product_id}
        isOtherSupplier={isOtherSupplier(item.supplierCode, supplierLock)}
        onOpen={handleOpenDetails}
        onRemove={handleRemovePick}
        onAdd={handleAddDirect}
        onReplace={handleOpenDetails}
      />
    ),
    [cart, supplierLock, handleRemovePick, handleOpenDetails, handleAddDirect, checkingProductId],
  );

  const isContinueDisabled = cartCount === 0 || !doesCartFit(cart);
  const isCatalogLoaderShown = isLoading || (isCatalogEmpty && isResultStale) || isAwaitingFilteredPage;
  const isItemCountShown = !isCatalogLoaderShown && itemCount > 0;
  const isHeadPillShown = !supplierLock;
  const isCountRowShown = isItemCountShown || isHeadPillShown;

  const continueFooterButton = useMemo(
    () => (
      <Button
        variant="contained"
        color="primary"
        onClick={handleContinue}
        disabled={isContinueDisabled}
        endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        fullWidth={isMobileViewport}
        size="medium"
      >
        {CONTINUE_TO_SHIPPING_LABEL}
      </Button>
    ),
    [handleContinue, isContinueDisabled, isMobileViewport],
  );

  const footerHelperText = useMemo(() => {
    const variant = isMobileViewport ? 'body2' : 'body1';

    if (cartCount > 0) {
      return (
        <Button variant="text" color="primary" onClick={handleOpenCart} sx={picksLinkSx}>
          {getItemsAddedText(cartCount)}
        </Button>
      );
    }

    return (
      <Typography variant={variant} color="text.secondary">
        {ADD_ITEM_TO_CONTINUE_TEXT}
      </Typography>
    );
  }, [cartCount, isMobileViewport, handleOpenCart]);

  // Only when there is a curated list to return to. The Footer hides it on mobile, where the
  // sticky toolbar already carries a back arrow.
  const backFooterButton = useMemo(() => {
    if (!hasPicksToReturnTo) {
      return undefined;
    }

    return (
      <Button variant="outlined" color="primary" onClick={handleBackToPicks} startIcon={<ChevronLeftIcon />}>
        Back
      </Button>
    );
  }, [hasPicksToReturnTo, handleBackToPicks]);

  const footerConfig = useMemo(
    () => ({
      backButton: backFooterButton,
      helperText: footerHelperText,
      submitButton: continueFooterButton,
    }),
    [backFooterButton, footerHelperText, continueFooterButton],
  );

  useFooter(footerConfig, selectionSectionRef);

  const scrollToTop = useCallback((behavior: ScrollBehavior) => window.scrollTo({ top: 0, behavior }), []);

  const fabConfig = useMemo(
    () => ({
      up: { label: BACK_TO_TOP_LABEL, onActivate: scrollToTop },
      desktopFabEnabled: true,
    }),
    [scrollToTop],
  );

  useFab(fabConfig);

  // Title owns the page's vertical rhythm; the row takes it over so the arrow centres on the text.
  const titleRowSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mt: mobile ? 3 : 4,
      mb: 2,
    }),
    [mobile],
  );

  const catalogContent = useMemo(() => {
    if (isCatalogLoaderShown) {
      return (
        <Box role="status" aria-label="Loading items" sx={loadingSx}>
          <CircularProgress />
        </Box>
      );
    }

    if (isCatalogEmpty) {
      return (
        <MarketplaceEmptyState
          error={error}
          hasSpentCredits={!isEmptiedBySupplierFilter && hasSpentCredits(resultContext.budget)}
          categoryName={resultCategoryName}
          hasSearch={isEmptiedBySupplierFilter || !!resultContext.query}
          hasPicksToReturnTo={hasPicksToReturnTo}
          onRetry={handleRetry}
          onOpenCart={handleOpenCart}
          onShowAllItems={handleShowAllItems}
          onShowAllCategories={handleShowAllCategories}
          onClearFilters={handleClearFilters}
          onBackToPicks={handleBackToPicks}
        />
      );
    }

    return (
      <Box sx={gridWrapperSx}>
        <GridView
          items={items}
          getItemKey={getProductKey}
          renderItem={renderCardItem}
          columns={MARKETPLACE_GRID_COLUMNS}
          gap={MARKETPLACE_GRID_GAP}
        />
        {isLoadingMore && (
          <Box role="status" aria-label="Loading more items" sx={loadingMoreSx}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    );
  }, [
    isCatalogLoaderShown,
    isCatalogEmpty,
    isEmptiedBySupplierFilter,
    error,
    resultContext,
    resultCategoryName,
    hasPicksToReturnTo,
    handleRetry,
    handleOpenCart,
    handleShowAllItems,
    handleShowAllCategories,
    handleClearFilters,
    handleBackToPicks,
    items,
    renderCardItem,
    isLoadingMore,
  ]);

  return (
    <AppWrapper>
      <Box sx={titleRowSx}>
        {hasPicksToReturnTo && (
          <IconButton onClick={handleBackToPicks} aria-label={BACK_ARIA_LABEL} sx={titleBackButtonSx}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
        <Title sx={{ my: 0 }}>{greetingTitle || BROWSE_MARKETPLACE_LABEL}</Title>
      </Box>

      <Subtitle variant="body2" sx={introTextSx}>
        {getBudgetIntroText(MARKETPLACE_TOTAL_CREDITS)} {MARKETPLACE_SHIPPING_INTRO_LEAD}{' '}
        <LocalShippingOutlinedIcon aria-hidden sx={introShippingIconSx} />
        <Box component="span" sx={introShippingTailSx}>
          {MARKETPLACE_SHIPPING_INTRO_TAIL}
        </Box>
      </Subtitle>

      <MarketplaceToolbar onOpenCart={handleOpenCart} isBackShown={hasPicksToReturnTo} onBack={handleBackToPicks} />

      <SupplierLockBanner lock={supplierLock} />

      {isCountRowShown && (
        <Box sx={countRowSx}>
          <Typography variant="body2" color="text.secondary">
            {isItemCountShown ? getItemCountText(itemCount) : ''}
          </Typography>
          {isHeadPillShown && <UsOnlyPill />}
        </Box>
      )}

      <Box ref={selectionSectionRef}>{catalogContent}</Box>

      <MarketplaceDetailsDrawer productId={openProductId} onClose={handleCloseDetails} />
      <MarketplaceCartDrawer open={isCartOpen} onClose={handleCloseCart} onContinue={handleContinue} />
      <UndoSnackbar
        key={replaceKey}
        open={!!replacedCart}
        message={picksReplacedText}
        onUndo={handleUndoReplace}
        onClose={handleCloseSnackbar}
      />
    </AppWrapper>
  );
};

export default Marketplace;
